import { useEffect, useState } from 'react';
import { useEquipment, useEquipmentDetail } from '@/lib/query';
import type { EquipmentRecord } from '@shared/domain';
import { fmt } from '@/lib/format';
import { Card, DataTable, EmptyHint, StatusPill } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

const LIFECYCLE_LABEL: Record<string, string> = { in_service: '在用', maintenance: '维保中', scrapped: '已报废' };
const LIFECYCLE_STYLE: Record<string, string> = {
  in_service: 'bg-ok-bg text-ok-fg border-ok-border',
  maintenance: 'bg-warn-bg text-warn-fg border-warn-border',
  scrapped: 'bg-alarm-bg text-alarm-fg border-alarm-border',
};

function ArchiveDrawer({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { data } = useEquipmentDetail(slug);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/20" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-title"
        className="h-full w-[420px] max-w-full overflow-y-auto bg-panel p-6 shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 id="archive-title" className="text-[18px] font-semibold text-slate-800">设备档案</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="关闭档案">✕</button>
        </div>
        <div className="mb-4 space-y-1 text-[14px]">
          <div className="text-[16px] font-semibold text-slate-800">{data.record.name}</div>
          <div className="text-slate-500">设备键：<span className="font-mono">{data.record.slug}</span></div>
          <div className="text-slate-500">类别：{data.record.category}</div>
          <div><span className={`pill ${LIFECYCLE_STYLE[data.record.lifecycle]}`}>{LIFECYCLE_LABEL[data.record.lifecycle]}</span></div>
        </div>
        <div className="mb-2 text-[14px] font-medium text-slate-700">统一生命周期时间轴</div>
        <ol className="mb-6 space-y-3 border-l border-hairline pl-4 text-[13px] text-slate-500">
          {['投运验收', '例行巡检', '维保录入', '当前状态'].map((s, i) => (
            <li key={s} className="relative">
              <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-brand-400" />
              {s} <span className="text-slate-300">· 2026-0{i + 1}</span>
            </li>
          ))}
        </ol>
        {data.snapshot ? (
          <>
            <div className="mb-2 text-[14px] font-medium text-slate-700">实时快照</div>
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-3 text-[13px]">
              {Object.values(data.snapshot.points).map((pt) => (
                <div key={pt.key} className="flex justify-between">
                  <span className="text-slate-500">{pt.label}</span>
                  <span className="tabular-nums">{fmt(pt.value, 2)} {pt.unit}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <EmptyHint>该设备无实时快照</EmptyHint>
        )}
      </div>
    </div>
  );
}

export default function Equipment() {
  const [tab, setTab] = useState<'ledger' | 'wo' | 'maint'>('ledger');
  const [lifecycle, setLifecycle] = useState('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { data } = useEquipment({ lifecycle: lifecycle === 'all' ? undefined : lifecycle, search: search || undefined });

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">设备管理（EAM）</h2>
        <p className="mt-1 text-[14px] text-slate-500">台账、工单调度、维保统计与设备档案的统一入口；后续可扩展备品备件库等模块。</p>
      </div>

      <div className="mb-5 flex gap-2">
        {([['ledger', '设备台账'], ['wo', '工单调度'], ['maint', '维保统计']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`btn ${tab === k ? 'btn-dark' : 'btn-ghost'}`}>{label}</button>
        ))}
      </div>

      {tab === 'ledger' ? (
        <>
          <Card className="mb-4 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <label className="text-[13px] text-slate-500 md:col-span-2">搜索
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="名称 / slug / 类别" className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
              </label>
              <label className="text-[13px] text-slate-500">生命周期
                <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm">
                  <option value="all">全部</option>
                  <option value="in_service">在用</option>
                  <option value="maintenance">维保中</option>
                  <option value="scrapped">已报废</option>
                </select>
              </label>
              <div className="flex items-end text-[13px] text-slate-400">
                共 {data?.total ?? 0} 条
              </div>
            </div>
          </Card>
          <Card className="p-0">
            <DataTable
              columns={[
                { key: 'name', header: '设备名称', render: (r: EquipmentRecord) => <span className="font-medium text-slate-800">{r.name}</span> },
                { key: 'slug', header: '设备键', render: (r: EquipmentRecord) => <span className="font-mono text-[13px] text-slate-500">{r.slug}</span> },
                { key: 'category', header: '类别' },
                { key: 'lifecycle', header: '生命周期', render: (r: EquipmentRecord) => <span className={`pill ${LIFECYCLE_STYLE[r.lifecycle]}`}>{LIFECYCLE_LABEL[r.lifecycle]}</span> },
                { key: 'ops', header: '操作', render: (r: EquipmentRecord) => <button onClick={() => setOpen(r.slug)} className="text-brand-600 hover:underline">打开档案</button> },
              ]}
              rows={data?.items ?? []}
              empty={<EmptyHint>无匹配设备</EmptyHint>}
            />
          </Card>
          <p className="mt-3 text-[12px] text-slate-400">以下列表来自后端 GET <code className="font-mono">/v1/equipment</code>（与 devices.json 种子同源）。</p>
          {open ? <ArchiveDrawer slug={open} onClose={() => setOpen(null)} /> : null}
        </>
      ) : (
        <Card className="p-10">
          <EmptyHint>
            <StatusPill health="offline" />
            {tab === 'wo' ? '工单调度模块骨架：接入后展示由报警转派与 ICM 生成的工单看板。' : '维保统计模块骨架：接入后展示 MTBF / 维保达成率等指标。'}
          </EmptyHint>
        </Card>
      )}
    </div>
  );
}
